import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, IconButton } from "../common";
import { Icon } from "@iconify/react";
import { Fragment, useEffect, useRef, useState } from "react";
import "./styles.css";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation, LanguageSwitcher } from "../../i18n";
import { useLocalePath } from "../../hooks/useLocalePath";
import { PAGE_GUTTER_X } from "../../layout/pageShell";

const Header = () => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [hideTopBar, setHideTopBar] = useState(false);
  const dropdownRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(lp("/login"));
  };

  // Scroll listener to hide top bar
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // scrolling down
        setHideTopBar(true);
      } else {
        // scrolling up
        setHideTopBar(false);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const mainNavItems = [
    { name: t("nav.home"), href: lp("/") },
    { name: t("nav.explore"), href: lp("/explore") },
    { name: t("nav.pricing"), href: lp("/pricing") },
    {
      name: t("nav.more"),
      submenus: [
        { name: t("nav.about"), href: lp("/about") },
        { name: t("nav.blog"), href: lp("/blog") },
        { name: t("nav.contact"), href: lp("/contact") },
        { name: t("nav.privacy"), href: lp("/privacy") },
        { name: t("nav.terms"), href: lp("/terms") },
      ],
    },
  ];

  const linkClassName = "transition-colors hover:text-primary font-medium";

  return (
    <header className="w-full lg:h-[144px] h-[172px] bg-white-gradient rounded-b-[30px] flex flex-col">
      <div
        className={`lg:h-[53px] h-[81px] bg-[#010F2C] rounded-b-[30px] flex flex-col items-center justify-center transition-all duration-500 ${
          hideTopBar
            ? "-translate-y-[100%] opacity-0"
            : "translate-y-0 opacity-100"
        }`}
      >
        <div className={`w-full max-w-page mx-auto ${PAGE_GUTTER_X} flex lg:flex-row flex-col items-center lg:justify-between justify-center gap-2`}>
          <div className="flex flex-row items-center lg:gap-[25px] gap-5">
            <div className="flex flex-row items-center gap-2">
              <Icon icon="mdi:email-outline" className="text-primary w-5 h-5" />
              <span className="text-white font-medium lg:text-sm md:text-sm text-[11px]">
                support@prombiz.co.il
              </span>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Icon
                icon="mdi:map-marker-outline"
                className="text-primary w-5 h-5"
              />
              <span className="text-white font-medium lg:text-sm md:text-sm text-[11px]">
                123 Business St. Tel Aviv, Israel
              </span>
            </div>
          </div>

          <div className="flex flex-row items-center gap-4">
            <LanguageSwitcher className="[&_button]:text-white [&_button]:text-xs" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center sticky z-40 top-0">
        <nav className={`w-full max-w-page mx-auto ${PAGE_GUTTER_X} flex flex-row items-center justify-between`}>
          <div className="flex flex-row items-center gap-16">
            <img
              src="/icons/logo.svg"
              alt="logo"
              className="w-[60px] h-[54px]"
            />

            <div className="lg:flex hidden flex-row items-center gap-6">
              {mainNavItems.map((item) => {
                // const isActiveParent = item.submenus?.some(
                //   (s) => pathname === s.href
                // );

                return item?.submenus?.length > 0 ? (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    ref={dropdownRef}
                  >
                    <button
                      className={`transition-colors font-medium flex flex-row items-center gap-1 ${
                        item.submenus.some((s) => pathname === s.href)
                          ? "text-primary"
                          : "hover:text-primary"
                      }`}
                    >
                      {item.name}
                      <Icon
                        icon={`mdi:chevron-${
                          openDropdown === item.name ? "up" : "down"
                        }`}
                        className="transition-all duration-300"
                      />
                    </button>

                    {openDropdown === item.name && (
                      <div className="list-none absolute left-0 top-[50px] mt-2 bg-white shadow-lg rounded-md py-2 z-50 min-w-[200px] animate-fadeIn">
                        {item.submenus.map((submenu) => {
                          const isActiveSubmenu = pathname === submenu.href;
                          return (
                            <Link
                              key={submenu.href}
                              to={submenu.href}
                              onClick={() => setOpenDropdown(null)}
                              className={`px-4 py-2 cursor-pointer transition-all duration-300 block font-medium ${
                                isActiveSubmenu
                                  ? "text-primary"
                                  : "hover:text-primary"
                              }`}
                            >
                              {submenu.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`transition-colors font-medium ${
                      pathname === item.href
                        ? "text-primary"
                        : "hover:text-primary"
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="lg:flex hidden flex-row items-center gap-[30px]">
              <div className="flex flex-row items-center gap-5">
                <Link to={lp("/explore")} className={linkClassName}>
                  {t("auth.forCustomers")}
                </Link>
                <Link to={lp("/auth/onboarding/business")} className={linkClassName}>
                  {t("auth.forBusiness")}
                </Link>
              </div>

              <div className="flex flex-row items-center gap-[15px]">
                <Button
                  variant="outline-primary"
                  label={t("auth.signIn")}
                  className="rounded-[30px]"
                  to={lp("/login")}
                />
                <Button
                  variant="outline-primary"
                  label={t("auth.hireAPro")}
                  className="rounded-[30px]"
                  to={lp("/register")}
                />
                <Button
                  variant="primary-gradient"
                  label={t("auth.getStarted")}
                  className="rounded-[15px]"
                  to={lp("/register")}
                />
              </div>
            </div>
          ) : (
            <div className="lg:flex hidden flex-row items-center gap-4">
              <Button
                variant="outline-primary"
                label={t("nav.dashboard")}
                icon="mdi:view-dashboard-outline"
                className="rounded-[30px]"
                to={user?.role === "business" ? lp("/dashboard") : lp("/client/dashboard")}
              />
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex flex-row items-center gap-2 group"
                >
                  <div className="w-9 h-9 rounded-full bg-primary-blur-01 border border-primary flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user?.firstName?.charAt(0) || user?.name?.charAt(0) || "U"}
                    </span>
                  </div>
                  <Icon icon="mdi:chevron-down" className={`w-4 h-4 text-dark-blue transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-[12px] shadow-lg border border-[#E5E7EB] z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#E5E7EB]">
                      <p className="text-sm font-semibold text-dark-blue truncate">
                        {user?.firstName || user?.name || "User"}
                      </p>
                      <p className="text-xs text-[#6A7282] truncate">{user?.email}</p>
                    </div>
                    <Link
                      to={user?.role === "business" ? lp("/dashboard") : lp("/client/dashboard")}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F4F5FA] transition-colors"
                    >
                      <Icon icon="mdi:view-dashboard-outline" className="w-4 h-4" />
                      {t("nav.dashboard")}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Icon icon="mdi:logout" className="w-4 h-4" />
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <IconButton
            variant="primary"
            icon={mobileMenuOpen ? "mdi:close" : "mdi:menu"}
            className="rounded-md w-[30px] h-[30px] lg:hidden"
            onClick={() => setMobileMenuOpen((v) => !v)}
          />
        </nav>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed top-0 right-0 w-[280px] h-full bg-white z-50 shadow-2xl flex flex-col lg:hidden animate-fadeIn">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <img src="/icons/logo.svg" alt="logo" className="w-[40px] h-[36px]" />
              <IconButton
                variant="secondary"
                icon="mdi:close"
                className="rounded-md w-[30px] h-[30px]"
                onClick={() => setMobileMenuOpen(false)}
              />
            </div>
            <nav className="flex-1 overflow-y-auto p-5 flex flex-col gap-1">
              {mainNavItems.map((item) =>
                item?.submenus?.length > 0 ? (
                  <Fragment key={item.name}>
                    {item.submenus.map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                          pathname === sub.href ? "text-primary bg-[#F4F5FA]" : "text-[#374151] hover:bg-[#F4F5FA] hover:text-primary"
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </Fragment>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      pathname === item.href ? "text-primary bg-[#F4F5FA]" : "text-[#374151] hover:bg-[#F4F5FA] hover:text-primary"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </nav>
            <div className="p-5 border-t border-[#E5E7EB] flex flex-col gap-3">
              {!isAuthenticated ? (
                <>
                  <Button
                    variant="outline-primary"
                    label={t("auth.signIn")}
                    className="rounded-[30px] w-full"
                    to={lp("/login")}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                  <Button
                    variant="primary-gradient"
                    label={t("auth.getStarted")}
                    className="rounded-[15px] w-full"
                    to={lp("/register")}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                </>
              ) : (
                <>
                  <Button
                    variant="outline-primary"
                    label={t("nav.dashboard")}
                    icon="mdi:view-dashboard-outline"
                    className="rounded-[30px] w-full"
                    to={user?.role === "business" ? lp("/dashboard") : lp("/client/dashboard")}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                  <Button
                    variant="outline-primary"
                    label={t("nav.logout")}
                    icon="mdi:logout"
                    className="rounded-[30px] w-full text-red-500 border-red-300"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                      navigate(lp("/login"));
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
